import React, { useEffect, useRef, useState } from 'react'

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import type { TChatRating, TSimChatRatingReason } from '@homepal/api-contract'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import LoadingSpinner from 'components_new/atoms/LoadingSpinner'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import ChatFeedbackPopover from 'components_new/molecules/ChatFeedbackPopover'
import ChatInput from 'components_new/molecules/ChatInput'

import { SIM_CHAT_RATING_REASONS } from 'types/GlobalChat'
import { NodeData } from 'types/transformation/types/nodes'
import { SimTable } from 'types/transformation/types/tables'

import {
  getSimTableChat,
  rateSimChatMessage,
  streamSimChat,
  TSimChatBoardChanges,
  TSimChatMessage
} from 'redux/api/DataPlatform/tables/v4/chat'

import ChatHistory from './ChatHistory'

// A proposal is validated before it reaches the board, so while it runs the
// label must not promise that the board changes.
const TOOL_LABELS: Record<string, string> = {
  list_source_tables: 'Söker källtabeller …',
  get_source_table_columns: 'Läser kolumner …',
  propose_transformation: 'Prövar en ändring på tavlan …',
  list_similar_transformations: 'Tittar på andra kunders modelleringar …',
  get_customer_transformation: 'Hämtar mall från annan kund …',
  list_own_transformations: 'Tittar på era andra tabeller …',
  get_own_transformation: 'Hämtar modellering från annan tabell …',
  preview_transformation: 'Förhandsgranskar resultatet …',
  query_source_data: 'Analyserar källdatat …',
  inspect_board: 'Kontrollerar tavlan …'
}

// A reply shown in the panel: the message plus what the server counted the
// reply doing to the board, which the assistant's own words may misstate,
// and the id the stored reply can be rated by.
type PanelMessage = TSimChatMessage & {
  board?: TSimChatBoardChanges | null
  messageId?: string | null
  rating?: TChatRating | null
}

const proposals = (count: number) =>
  (count === 1 ? '1 förslag' : `${count} förslag`)

// What the board status line says, or null when the reply never tried to
// change the board.
function boardStatusLabel(board: TSimChatBoardChanges): string | null {
  const { proposals_accepted: accepted, proposals_rejected: rejected } = board

  if (accepted === 0 && rejected === 0) return null

  if (accepted === 0) {
    return rejected === 1
      ? 'Tavlan oförändrad · förslaget avvisades'
      : `Tavlan oförändrad · ${proposals(rejected)} avvisades`
  }

  const rejectedPart = rejected > 0
    ? ` · ${proposals(rejected)} avvisades`
    : ''

  return `Tavlan ändrad · ${proposals(accepted)}${rejectedPart}`
}

const INTERRUPTED =
  'Svaret avbröts innan det var klart. Det som hann föreslås ligger kvar '
  + 'på tavlan — skriv "fortsätt" så arbetar jag vidare.'

const EMPTY_STATE =
  'Beskriv hur datan ska modelleras — t.ex. vilken källtabell som ska '
  + 'användas som input, hur tabeller ska joinas och vilka kolumner som '
  + 'ska mappas. Förslaget läggs på tavlan och sparas först när du '
  + 'trycker Spara.'

// The server's count of what the reply did to the board, under the reply, so
// the user can tell a change made from one the assistant only describes.
const BoardStatus = (props: { board?: TSimChatBoardChanges | null }) => {
  const label = props.board ? boardStatusLabel(props.board) : null

  if (!label) return null

  const changed = (props.board?.proposals_accepted ?? 0) > 0

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, mt: 0.5 }}>
      <Icon
        color={changed ? 'success' : 'warning'}
        fontSize="inherit"
        name={changed ? 'CheckCircleOutline' : 'ErrorOutline'}
      />
      <Text color="text.secondary" variant="caption">
        {label}
      </Text>
    </Box>
  )
}

interface ChatPanelProps {
  table: SimTable
  onClose: () => void
  getBoardNodes: () => NodeData[]
  onProposal: (nodes: NodeData[], summary: string | null) => void
}

// The thumbs under a reply. They lock once rated: feedback is write-once.
const ReplyRating = (props: {
  message: PanelMessage
  onRateUp: () => void
  onRateDown: (event: React.MouseEvent<HTMLElement>) => void
}) => {
  const { message, onRateUp, onRateDown } = props

  if (!message.messageId) return null

  const rated = Boolean(message.rating)

  return (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
      <Tooltip title="Bra svar">
        <span>
          <IconButton disabled={rated} onClick={onRateUp} size="small">
            <Icon
              color={message.rating === 'up' ? 'primary' : 'inherit'}
              fontSize="small"
              name={
                message.rating === 'up' ? 'ThumbUpAlt' : 'ThumbUpAltOutlined'
              }
            />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Dåligt svar">
        <span>
          <IconButton disabled={rated} onClick={onRateDown} size="small">
            <Icon
              color={message.rating === 'down' ? 'primary' : 'inherit'}
              fontSize="small"
              name={
                message.rating === 'down'
                  ? 'ThumbDownAlt'
                  : 'ThumbDownAltOutlined'
              }
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

const ChatPanel = (props: ChatPanelProps) => {
  const { table, onClose, getBoardNodes, onProposal } = props

  const [messages, setMessages] = useState<PanelMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [workingLabel, setWorkingLabel] = useState<string | null>(null)

  // The stored chat the conversation continues on the server. Null until
  // the first reply has been stored or an earlier chat is opened.
  const [chatId, setChatId] = useState<string | null>(null)

  const [browsingHistory, setBrowsingHistory] = useState(false)
  const [openingChat, setOpeningChat] = useState(false)

  // The chat being opened, so a slower load of an earlier choice is dropped.
  const resumeRef = useRef<string | null>(null)

  const [feedbackFor, setFeedbackFor] = useState<number | null>(null)
  const [feedbackAnchor, setFeedbackAnchor] = useState<HTMLElement | null>(
    null
  )

  const abortRef = useRef<AbortController | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, workingLabel])

  // Abort an in-flight stream when the panel unmounts.
  useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  const patchLastAssistantMessage = (
    patch: (content: string) => string,
    extra: Partial<PanelMessage> = {}
  ) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1]

      if (!last || last.role !== 'assistant') return prev

      return [
        ...prev.slice(0, -1),
        { ...last, content: patch(last.content), ...extra }
      ]
    })
  }

  const setRating = (index: number, rating: TChatRating | null) =>
    setMessages((prev) =>
      prev.map((message, i) => (i === index ? { ...message, rating } : message))
    )

  // The thumb colours at once, but a lost call must not leave it coloured:
  // the thumbs lock once rated, so the user could neither try again nor
  // tell the rating never landed.
  const submitRating = (
    index: number,
    rating: TChatRating,
    reason: TSimChatRatingReason | null = null,
    comment: string | null = null
  ) => {
    const message = messages[index]

    if (!chatId || !message?.messageId || message.rating) return

    setRating(index, rating)

    rateSimChatMessage(table.id, chatId, message.messageId, {
      rating,
      rating_reason: reason,
      rating_comment: comment
    }).catch((e) => {
      // A 409 means the rating is already recorded, so the thumb is right.
      if (e?.response?.status === 409) return

      setRating(index, null)
    })
  }

  // Back to an empty panel, which starts a new stored chat on the next
  // message.
  const startNewChat = () => {
    abortRef.current?.abort()
    resumeRef.current = null
    setMessages([])
    setChatId(null)
    setStreaming(false)
    setWorkingLabel(null)
    setOpeningChat(false)
    setBrowsingHistory(false)
  }

  // Replaces the panel with an earlier chat; the next message continues it.
  const openChat = (id: string) => {
    abortRef.current?.abort()
    resumeRef.current = id
    setMessages([])
    setChatId(null)
    setStreaming(false)
    setWorkingLabel(null)
    setBrowsingHistory(false)
    setOpeningChat(true)

    getSimTableChat(table.id, id)
      .then((response) => {
        if (resumeRef.current !== id) return

        const chat = response.data.data

        setMessages(
          chat.messages.map((message) => ({
            role: message.role,
            content: message.content,
            board: message.board,
            messageId: message.id,
            rating: message.rating
          }))
        )
        setChatId(chat.id)
      })
      .catch(() => {
        if (resumeRef.current !== id) return

        setMessages([
          {
            role: 'assistant',
            content: 'Kunde inte öppna chatten. Försök igen.'
          }
        ])
      })
      .finally(() => {
        if (resumeRef.current === id) setOpeningChat(false)
      })
  }

  const openFeedback = (
    index: number,
    event: React.MouseEvent<HTMLElement>
  ) => {
    setFeedbackFor(index)
    setFeedbackAnchor(event.currentTarget)
  }

  const closeFeedback = () => {
    setFeedbackFor(null)
    setFeedbackAnchor(null)
  }

  const handleSend = () => {
    const message = input.trim()

    if (!message || streaming || openingChat) return

    // The server takes the conversation as role and content only.
    const history: TSimChatMessage[] = messages.map(({ role, content }) => ({
      role,
      content
    }))

    setMessages([
      ...messages,
      { role: 'user', content: message },
      { role: 'assistant', content: '' }
    ])
    setInput('')
    setStreaming(true)

    const controller = new AbortController()

    abortRef.current = controller

    const finish = () => {
      setStreaming(false)
      setWorkingLabel(null)
    }

    streamSimChat(
      table.id,
      {
        message,
        history,
        nodes: getBoardNodes(),
        ...(chatId ? { chat_id: chatId } : {})
      },
      {
        onText: (delta) => patchLastAssistantMessage((c) => c + delta),
        onToolStart: (step) =>
          setWorkingLabel(TOOL_LABELS[step.tool] ?? 'Arbetar …'),
        onToolEnd: () => setWorkingLabel(null),
        onTransformation: (payload) =>
          onProposal(payload.nodes, payload.summary),
        onDone: (result) => {
          if (result.chat_id) setChatId(result.chat_id)

          patchLastAssistantMessage(() => result.reply, {
            board: result.board,
            messageId: result.message_id
          })
          finish()
        },
        onError: (errorMessage) => {
          patchLastAssistantMessage(
            (c) => c || `Något gick fel: ${errorMessage}`
          )
          finish()
        },
        onInterrupted: () => {
          patchLastAssistantMessage((c) =>
            c ? `${c}\n\n_${INTERRUPTED}_` : INTERRUPTED
          )
          finish()
        }
      },
      controller.signal
    )
  }

  return (
    <Box
      sx={{
        bgcolor: 'common.white',
        borderLeft: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: 380
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <Icon name="AI" fontSize="small" color="inherit" />
          <Text fontWeight="700" variant="subtitle1">
            AI-assistent
          </Text>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {messages.length > 0 || chatId ? (
            <Tooltip title="Ny chatt">
              <IconButton onClick={startNewChat} size="small">
                <Icon name="AddOutlined" fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Tidigare chattar">
            <IconButton
              onClick={() => setBrowsingHistory((value) => !value)}
              size="small"
            >
              <Icon name="History" fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <Icon name="Close" fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {browsingHistory ? (
        <ChatHistory
          onBack={() => setBrowsingHistory(false)}
          onSelect={openChat}
          tableId={table.id}
        />
      ) : null}

      <Box
        sx={{
          display: browsingHistory ? 'none' : 'block',
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 1.5
        }}
      >
        {openingChat ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <LoadingSpinner size={20} />
          </Box>
        ) : null}

        {messages.length === 0 && !openingChat ? (
          <Text color="text.secondary" variant="body2">
            {EMPTY_STATE}
          </Text>
        ) : (
          messages.map((message, index) =>
            message.role === 'user' ? (
              <Box
                key={index}
                sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}
              >
                <Box
                  sx={{
                    bgcolor: 'action.selected',
                    borderRadius: 2,
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1
                  }}
                >
                  <Text variant="body2">{message.content}</Text>
                </Box>
              </Box>
            ) : (
              <Box
                key={index}
                sx={{
                  mb: 1.5,
                  '& p': { my: 0.5 },
                  fontSize: '0.875rem'
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.content}
                </ReactMarkdown>
                <BoardStatus board={message.board} />
                <ReplyRating
                  message={message}
                  onRateDown={(event) => openFeedback(index, event)}
                  onRateUp={() => submitRating(index, 'up')}
                />
              </Box>
            )
          )
        )}

        {streaming && (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
            <LoadingSpinner size={16} />
            <Text color="text.secondary" variant="caption">
              {workingLabel ?? 'Tänker …'}
            </Text>
          </Box>
        )}

        <div ref={bottomRef} />
      </Box>

      <ChatFeedbackPopover
        anchorEl={feedbackAnchor}
        onClose={closeFeedback}
        onSubmit={(reason, comment) => {
          if (feedbackFor !== null) {
            submitRating(feedbackFor, 'down', reason, comment)
          }

          closeFeedback()
        }}
        reasons={SIM_CHAT_RATING_REASONS}
      />

      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          display: browsingHistory ? 'none' : 'block',
          p: 1.5
        }}
      >
        <ChatInput
          autoFocus={true}
          disabled={streaming || openingChat}
          multiline={true}
          onChange={setInput}
          onSubmit={handleSend}
          placeholder="Beskriv hur datan ska modelleras …"
          value={input}
        />
      </Box>
    </Box>
  )
}

export default ChatPanel
